import _ from 'underscore';

import { ListResult } from '../../ts/fetchers/ListResult';
import { Set } from '../../ts/models/Set';
import { IUser } from '../../ts/ServerApi/types/IUser';
import { IResultCodes, IUserActionResult } from '../../ts/ServerApi/types/IUserActionResult';
import { IContentTypeError } from '../serverApi/types/IContentTypeError';
import { IError } from '../serverApi/types/IError';
import { IMultipleContentTypeResult,IResult } from '../serverApi/types/IResponse';

export interface IImportUsersResult extends IResult {
  users: IUser[];
}

export module ResultHelper {

  /**
   * Merges lists of properties from a list of result objects (typically from an action that was
   * split into multiple requests) into a single list of properties.
   * @param results An array of result objects from the server
   * @param getProperty A function that extracts the relevant property from each result item
   * @returns {T[]}
   */
  export function combine<TResult, TProperty>(
      results: TResult[],
      getProperty: (result: TResult) => TProperty[]
  ): TProperty[] {
    return _.compact(_.flatten(_.map(_.compact(results), getProperty)));
  }

  /**
   * Merge objects from a list of result objects (typically from an action that was split into
   * multiple requests) by combining all the keys into a single object. If the same key appears
   * more than once in the source objects, the later one will take precedence.
   * @param results An array of result objects from the server
   * @param getProperty A function that extracts the relevant property from each result item
   * @returns {any}
   */
  export function combineByKey<TResult, TProperty>(
      results: TResult[],
      getProperty: (result: TResult) => _.Dictionary<TProperty>
  ): _.Dictionary<TProperty> {
    const allProperties: _.Dictionary<TProperty>[] = _.compact(_.map(_.compact(results), getProperty));
    allProperties.unshift({}); // Create the args array for _.extend
    return _.extend.apply(null, allProperties);
  }

  export function combineErrors(results: IResult[]): IError[] {
    return combine(results, (result) => result.errors);
  }

  export function combineIds(results: { ids: string[] }[]): string[] {
    return combine(results, (result) => result.ids);
  }

  export function combineUsers(results: IUserActionResult[]): IUser[] {
    return combine(results, (result) => result.users);
  }

  export function combineResults(results: IResult[]): IResult {
    return {
      errors: combineErrors(results)
    };
  }

  export function combineContentTypeErrors(results: IMultipleContentTypeResult[]): IContentTypeError[] {
    return combine(results, (result) => result.errors);
  }

  export function combineMultipleContentTypeResults(results: IMultipleContentTypeResult[]): IMultipleContentTypeResult {
    return {
      errors: combineErrors(results)
    };
  }

  /**
   * Combines items from multiple list results into a single list. Does not guarantee a specific order.
   */
  export function combineListResultItems<T>(results: ListResult<T>[]): T[] {
    return combine(results, (result) => result.items);
  }

  export function combineCodes(results: IUserActionResult[]): IResultCodes[] {
    return combine(results, (result) => result.results);
  }

  export function combineUserResults(results: IUserActionResult[]): IUserActionResult {
    return {
      errors: combineErrors(results),
      users: combineUsers(results),
      results: combineCodes(results)
    };
  }

  export function combineImportUserResults(results: IImportUsersResult[]): IImportUsersResult {
    return {
      errors: combineErrors(results),
      users: combineUsers(results)
    };
  }

  export function hasErrors(result: IResult) {
    return !!(result && result.errors) && result.errors.length > 0;
  }

  // Reject a promise if the result returned from the server contains any errors.
  export function rejectResultOnError(): (result: IResult) => Promise<IResult> {
    return (result: IResult) => {
      return hasErrors(result)
        ? Promise.reject(result)
        : Promise.resolve(result);
    };
  }

  export function getErrorIds(result: IResult, options: { excludeCode?: number } = {}): Set<string> {
    return Set.fromKeyArray(_.compact(_.map(result && result.errors || [],
      (error: IError) => error.code === options.excludeCode ? null : error.id
    )));
  }

  export function getErrorIdsWithCode(result: IResult, code: number): Set<string> {
    return Set.fromKeyArray(_.compact(_.map(result && result.errors || [],
      (error: IError) => error.code === code ? error.id : null
    )));
  }

  export function getErrorCodes(result: IResult): Set<number> {
    return Set.fromNumberArray(_.compact(_.map(result && result.errors || [],
      (error: IError) => error.code
    )));
  }

}
